/**
 * Analytics consent. Nothing from Google loads, and no analytics cookie is set, until the visitor accepts.
 *
 * - The choice is stored on this device only (localStorage key below), with a version so a change to what
 *   we collect can ask again.
 * - A browser that sends the Global Privacy Control signal is treated as "declined" without asking.
 * - Google Consent Mode v2 defaults are set to denied before anything is loaded.
 * - Declining or withdrawing removes the _ga cookies this site set.
 */

export type ConsentChoice = 'granted' | 'denied';

const KEY = 'flahafast.consent';
const VERSION = 1;
export const OPEN_CONSENT_EVENT = 'flahafast:open-consent';
export const CONSENT_CHANGED_EVENT = 'flahafast:consent-changed';

const GA_ID: string = (import.meta as any).env?.VITE_GA_ID || 'G-YX5KE78HRV';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

export function globalPrivacyControl(): boolean {
  return typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;
}

export function getConsent(): ConsentChoice | null {
  if (globalPrivacyControl()) return 'denied';
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.version === VERSION && (parsed.choice === 'granted' || parsed.choice === 'denied') ? parsed.choice : null;
  } catch {
    return null;
  }
}

/** Remove the cookies Google Analytics sets (_ga and _ga_<container>) from this site. */
export function removeAnalyticsCookies(): void {
  if (typeof document === 'undefined') return;
  const hosts = new Set<string>([location.hostname]);
  const parts = location.hostname.split('.');
  for (let i = 1; i < parts.length - 1; i++) hosts.add('.' + parts.slice(i).join('.'));
  for (const raw of document.cookie.split(';')) {
    const name = raw.split('=')[0].trim();
    if (name !== '_ga' && !name.startsWith('_ga_') && name !== '_gid') continue;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    for (const host of hosts) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
  }
}

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
}

let loaded = false;

/** Load Google Analytics. Only ever called after the visitor has accepted. */
export function loadAnalytics(): void {
  if (loaded || typeof document === 'undefined') return;
  loaded = true;
  ensureGtag();
  window.gtag!('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'denied' });
  window.gtag!('js', new Date());
  window.gtag!('consent', 'update', { analytics_storage: 'granted' });
  window.gtag!('config', GA_ID, { anonymize_ip: true, allow_google_signals: false, allow_ad_personalization_signals: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(script);
}

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ choice, version: VERSION, at: new Date().toISOString() }));
  } catch {
    /* storage unavailable: the choice applies for this page view only */
  }
  applyConsent(choice);
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
}

function applyConsent(choice: ConsentChoice | null): void {
  if (choice === 'granted') {
    window[`ga-disable-${GA_ID}`] = false;
    loadAnalytics();
    return;
  }
  window[`ga-disable-${GA_ID}`] = true;
  if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'denied' });
  removeAnalyticsCookies();
}

/** Call once at startup: applies a remembered choice. With no choice, nothing loads. */
export function initConsent(): ConsentChoice | null {
  const choice = getConsent();
  applyConsent(choice);
  return choice;
}

/** Test hook: forget that analytics was loaded in this page. */
export function __resetForTests(): void {
  loaded = false;
}
